/**
 * MBA Lab — mode-as-data for the Compassionate Accountability Planner.
 * Pure data, no functions: planner-model.js and accountability-planner.js
 * both read this, and the Razor view never branches on mode - every piece of
 * mode-specific copy lives here so a new mode never needs a view change.
 *
 * Fields per mode:
 * - id/label/tagline/intro: identity + framing copy for the mode-picker card.
 * - suggestedOpening/suggestedQuestions/suggestedSupport: editable starter
 *   text seeded into the matching textarea - a draft to edit, not a fill-in-
 *   the-blank template the tool pretends is personalized.
 * - emphasizedCauses: SYSTEM_CAUSES ids this situation commonly involves -
 *   shown as a hint near the diagnosis menu, never pre-checked for the user.
 * - linkedTools: cross-links surfaced in this mode's output (e.g. team-reset
 *   hands off to the team charter builder).
 * - safetyEmphasis: SAFETY_CATEGORIES ids worth double-checking for this
 *   situation - a hint, not an auto-check; the user's own answers are what
 *   actually drive safetyScreen().
 * - rubric: only draft-message-review carries this - a fixed self-review
 *   checklist shown for that mode, per the plan's "deterministic rubric
 *   checklist always" requirement.
 */

export const MODES = Object.freeze([
  {
    id: 'difficult-performance-feedback',
    label: 'Difficult performance feedback',
    tagline: 'Prepare to name a specific gap without losing the relationship.',
    intro: 'For when someone\'s work has slipped and you need to name it clearly, before it becomes a bigger problem.',
    suggestedOpening: 'I want to talk through something specific I\'ve noticed - I want to understand what\'s going on before we figure out next steps together.',
    suggestedQuestions: 'What\'s your read on how this has been going lately? What\'s making this harder than usual right now?',
    suggestedSupport: 'Are there resources, time, or clarity that would make this easier to hit going forward?',
    emphasizedCauses: ['capacity', 'capability', 'unclearExpectations'],
    linkedTools: [],
    safetyEmphasis: ['formalDisciplinary'],
    rubric: null,
  },
  {
    id: 'missed-commitment',
    label: 'Missed commitment',
    tagline: 'Find out what actually happened before deciding what it means.',
    intro: 'For when someone didn\'t follow through on something they agreed to, and you don\'t yet know why.',
    suggestedOpening: 'I noticed this didn\'t happen by when we agreed - I want to understand what got in the way.',
    suggestedQuestions: 'What happened between when we agreed to this and now? Was anything unclear, or did something else take priority?',
    suggestedSupport: 'What would make it easier to flag early next time if something\'s at risk of slipping?',
    emphasizedCauses: ['conflictingPriorities', 'capacity', 'missingDecisionAuthority'],
    linkedTools: [],
    safetyEmphasis: [],
    rubric: null,
  },
  {
    id: 'conflict-between-colleagues',
    label: 'Conflict between colleagues',
    tagline: 'Separate facts, interests, and structure before you referee anything.',
    intro: 'For tension between two people that\'s starting to affect the people around them.',
    suggestedOpening: 'I want to understand what\'s going on between you two - I\'m not here to assign blame, I want to understand what each of you actually needs.',
    suggestedQuestions: 'What\'s the actual disagreement, separate from how it\'s been showing up? What would a good outcome look like to you? Is there a decision-rights or role question underneath this?',
    suggestedSupport: 'Would a structured joint conversation help, or does each person need space first?',
    emphasizedCauses: ['missingDecisionAuthority', 'roleDesign', 'conflictingPriorities'],
    linkedTools: [],
    safetyEmphasis: ['harassment', 'retaliation'],
    rubric: null,
    conflictBreakdownPrompt: 'Separate what you know: the facts both sides would agree on, each person\'s interests underneath their stated position, any identity/status threat or power difference in play, and whether a role or decision-right ambiguity is the real structural cause.',
  },
  {
    id: 'unclear-expectations',
    label: 'Unclear expectations',
    tagline: 'Make the standard explicit before holding anyone to it.',
    intro: 'For when a working relationship keeps hitting friction because what "good" looks like was never actually agreed on.',
    suggestedOpening: 'I think we might have different pictures of what this should look like - I want to get us aligned.',
    suggestedQuestions: 'What\'s your understanding of what\'s expected here? Where do you think our pictures might differ?',
    suggestedSupport: 'Would it help to write the standard down together so it\'s not just in either of our heads?',
    emphasizedCauses: ['unclearExpectations', 'roleDesign'],
    linkedTools: [{ slug: 'team-charter-builder', label: 'Team Charter Builder' }],
    safetyEmphasis: [],
    rubric: null,
  },
  {
    id: 'workload-burnout-concern',
    label: 'Workload / burnout concern',
    tagline: 'Ask before you assume, and know when this is beyond a manager conversation.',
    intro: 'For when someone\'s showing signs of being overwhelmed and you want to check in without diagnosing them yourself.',
    suggestedOpening: 'I\'ve noticed some things lately, and I wanted to check in - not to assign blame, just to understand what\'s going on.',
    suggestedQuestions: 'How are you doing with everything on your plate right now? Is there anything that would genuinely lighten the load?',
    suggestedSupport: 'Would redistributing some work help, or is this about something else?',
    emphasizedCauses: ['capacity', 'resources', 'conflictingPriorities'],
    linkedTools: [],
    safetyEmphasis: ['mentalHealthEmergency', 'protectedLeaveAccommodation'],
    rubric: null,
  },
  {
    id: 'change-announcement',
    label: 'Change announcement',
    tagline: 'Explain the real reason, including the tradeoffs, before people fill the silence themselves.',
    intro: 'For communicating an organizational or process change to the people it affects.',
    suggestedOpening: 'I want to walk you through a change that\'s coming, why it\'s happening, and what it means for you specifically.',
    suggestedQuestions: 'What\'s your first reaction to this? What questions does it raise that I haven\'t answered yet?',
    suggestedSupport: 'What would help you adjust to this - training, time, or something else?',
    emphasizedCauses: ['unclearExpectations', 'coordinationFailures'],
    linkedTools: [],
    safetyEmphasis: ['layoff'],
    rubric: null,
  },
  {
    id: 'ai-implementation-resistance',
    label: 'AI implementation resistance',
    tagline: 'Find out which problem this actually is before responding to it.',
    intro: 'For when someone is resisting a new AI tool or process, and the real cause could be several very different things.',
    suggestedOpening: 'I\'ve noticed some hesitation about this - I want to understand what\'s actually behind it.',
    suggestedQuestions: 'What\'s your biggest concern with this - how it works, what it means for your role, or something else?',
    suggestedSupport: 'What would make this easier to trust or adopt - more training, more transparency into how it works, something else?',
    emphasizedCauses: ['capability', 'motivation'],
    linkedTools: [{ slug: 'change-readiness-assessment', label: 'Change and AI Readiness Assessment' }],
    safetyEmphasis: [],
    rubric: null,
  },
  {
    id: 'crisis-communication',
    label: 'Crisis communication',
    tagline: 'Say what you know, what you don\'t, and when they\'ll hear more.',
    intro: 'For communicating during an active problem, before you have the complete picture.',
    suggestedOpening: 'Here\'s what we know right now, what we\'re still finding out, and when I\'ll update you next.',
    suggestedQuestions: 'What do people most need to know right now to do their jobs? What are they likely assuming that isn\'t true?',
    suggestedSupport: 'Who else needs to hear this directly from you, rather than secondhand?',
    emphasizedCauses: ['coordinationFailures', 'missingDecisionAuthority'],
    linkedTools: [],
    safetyEmphasis: ['violenceSafety', 'legalRegulatory'],
    rubric: null,
  },
  {
    id: 'team-reset',
    label: 'Team reset',
    tagline: 'Name what\'s not working, then rebuild the agreement together.',
    intro: 'For when a team\'s working agreements have quietly broken down and need to be rebuilt, not just re-announced.',
    suggestedOpening: 'I want us to talk honestly about what\'s not working right now, and rebuild how we work together from there.',
    suggestedQuestions: 'What\'s one thing about how we work together that isn\'t working for you? What would you want to keep?',
    suggestedSupport: 'Once you\'ve named what\'s not working, the team charter builder can help turn the new agreement into something concrete.',
    emphasizedCauses: ['roleDesign', 'coordinationFailures', 'missingDecisionAuthority'],
    linkedTools: [{ slug: 'team-charter-builder', label: 'Team Charter Builder' }],
    safetyEmphasis: [],
    rubric: null,
  },
  {
    id: 'one-on-one-planning',
    label: 'One-on-one planning',
    tagline: 'A real agenda, not just a status update.',
    intro: 'For preparing a regular one-on-one so it covers more than task status.',
    suggestedOpening: 'Before we dive into status - how are you doing, actually?',
    suggestedQuestions: 'What\'s going well? What\'s an obstacle right now? What decision are you waiting on from me?',
    suggestedSupport: 'What development or growth conversation is overdue?',
    emphasizedCauses: [],
    linkedTools: [{ slug: 'leadership-practice-journal', label: 'Leadership Practice Journal' }],
    safetyEmphasis: [],
    rubric: null,
    agendaTopics: ['Goals & progress', 'Obstacles', 'Workload & capacity', 'Feedback', 'Development', 'Relationship health', 'Decisions needed', 'Commitments & follow-up'],
  },
  {
    id: 'draft-message-review',
    label: 'Draft message review',
    tagline: 'Run your own message through a clear-headed checklist before you send it.',
    intro: 'For reviewing a difficult message you\'ve already drafted, before you send it.',
    suggestedOpening: '',
    suggestedQuestions: '',
    suggestedSupport: '',
    emphasizedCauses: [],
    linkedTools: [],
    safetyEmphasis: [],
    rubric: [
      'Does this lead with curiosity rather than accusation?',
      'Have I separated facts from my interpretation of them?',
      'Is the standard and impact specific, not vague?',
      'Have I left room for their perspective, not just stated mine?',
      'Would I be comfortable if this were read aloud to them in person?',
      'Have I avoided softening this so much that the real message gets lost?',
    ],
  },
]);

export const MODES_BY_ID = new Map(MODES.map((m) => [m.id, m]));
export const DEFAULT_MODE_ID = MODES[0].id;

export function isValidModeId(id) {
  return MODES_BY_ID.has(id);
}
